/**
 * BlockEditorPanel — the manual editing surface of the Signature Studio.
 * Templates, global styles, the row/column/block layout tree, and a property
 * form for the selected block. Every change routes through the page's
 * mutation handlers so undo history and the live preview stay in sync.
 */

import React, { useMemo } from 'react';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Checkbox,
  Divider,
  Flex,
  HStack,
  IconButton,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Select,
  Text,
  Textarea,
  Tooltip,
  useColorModeValue,
  VStack,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import {
  FiAlignLeft,
  FiArrowDown,
  FiArrowUp,
  FiChevronDown,
  FiImage,
  FiLink,
  FiMinus,
  FiMoreVertical,
  FiMousePointer,
  FiPhone,
  FiPlus,
  FiSettings,
  FiShare2,
  FiTrash2,
  FiType,
} from 'react-icons/fi';
import {
  BLOCK_TYPES,
  CONTACT_KINDS,
  findBlock,
  LIMITS,
  SOCIAL_NETWORKS,
  TEMPLATES,
  WEB_SAFE_FONTS,
} from './signatureSpec';

const BLOCK_META = {
  image: { label: 'Image', icon: FiImage },
  text: { label: 'Text', icon: FiType },
  contact: { label: 'Contact line', icon: FiPhone },
  social: { label: 'Social links', icon: FiShare2 },
  button: { label: 'Button', icon: FiMousePointer },
  divider: { label: 'Divider', icon: FiMinus },
  spacer: { label: 'Spacer', icon: FiAlignLeft },
  disclaimer: { label: 'Disclaimer', icon: FiType },
};

function blockSummary(block) {
  switch (block.type) {
    case 'image': return block.alt || (block.src ? 'Image' : 'Image (pick one)');
    case 'text': return block.text ? block.text.slice(0, 26) : 'Empty text';
    case 'contact': return (block.items || []).map((i) => i.label).join(' | ').slice(0, 26) || 'No items yet';
    case 'social': return `${(block.icons || []).length} link(s)`;
    case 'button': return block.text || 'Button';
    case 'divider': return 'Divider';
    case 'spacer': return `Spacer ${block.height}px`;
    case 'disclaimer': return block.text ? block.text.slice(0, 26) : 'Disclaimer';
    default: return block.type;
  }
}

// ---------------------------------------------------------------------------
// Small field helpers
// ---------------------------------------------------------------------------

function Field({ label, children, flex }) {
  return (
    <Box flex={flex}>
      <Text fontSize="xs" fontWeight="semibold" mb={1} opacity={0.75}>{label}</Text>
      {children}
    </Box>
  );
}

function NumField({ label, value, onChange, min = 0, max = 999, flex }) {
  return (
    <Field label={label} flex={flex}>
      <NumberInput
        size="xs"
        value={value ?? ''}
        min={min}
        max={max}
        onChange={(_, n) => { if (Number.isFinite(n)) onChange(n); }}
      >
        <NumberInputField />
        <NumberInputStepper>
          <NumberIncrementStepper />
          <NumberDecrementStepper />
        </NumberInputStepper>
      </NumberInput>
    </Field>
  );
}

function ColorField({ label, value, onChange, allowEmpty = false, flex }) {
  return (
    <Field label={label} flex={flex}>
      <HStack spacing={1}>
        <input
          type="color"
          value={value || '#333333'}
          onChange={(e) => onChange(e.target.value)}
          style={{ width: 26, height: 24, padding: 0, border: 'none', background: 'transparent', cursor: 'pointer' }}
        />
        <Input
          size="xs"
          value={value || ''}
          placeholder={allowEmpty ? 'default' : '#333333'}
          onChange={(e) => {
            const v = e.target.value.trim();
            if (allowEmpty && v === '') onChange('');
            else if (/^#[0-9a-fA-F]{6}$/.test(v)) onChange(v);
          }}
          maxW="84px"
        />
      </HStack>
    </Field>
  );
}

function TextField({ label, value, onChange, placeholder, flex }) {
  return (
    <Field label={label} flex={flex}>
      <Input size="xs" value={value || ''} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
    </Field>
  );
}

function SelectField({ label, value, onChange, options, flex }) {
  return (
    <Field label={label} flex={flex}>
      <Select size="xs" value={value} onChange={(e) => onChange(e.target.value)}>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </Select>
    </Field>
  );
}

// ---------------------------------------------------------------------------
// Per-type block forms
// ---------------------------------------------------------------------------

function ImageForm({ block, images, onChange }) {
  const options = images.map((img) => ({ value: img.url, label: img.name }));
  const libEntry = images.find((i) => i.url === block.src);
  // Imported signatures reference images hosted elsewhere — keep them selectable.
  const isExternal = Boolean(block.src) && !libEntry;
  const ratio = libEntry && libEntry.width ? (libEntry.height / libEntry.width) : (block.width ? block.height / block.width : 1);
  const naturalSize = block.width == null && block.height == null;
  return (
    <VStack align="stretch" spacing={2}>
      <Field label="Image (from your library)">
        <Select
          size="xs"
          value={block.src || ''}
          onChange={(e) => {
            const img = images.find((i) => i.url === e.target.value);
            if (img) {
              const w = Math.min(img.width || block.width || 96, 300);
              onChange({ src: img.url, alt: img.name, width: w, height: Math.round(w * ((img.height || w) / (img.width || w))) });
            } else {
              onChange({ src: e.target.value });
            }
          }}
        >
          <option value="">— none —</option>
          {isExternal && <option value={block.src}>Imported image (hosted elsewhere)</option>}
          {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </Select>
      </Field>
      {!block.src && (
        <Text fontSize="xs" color="orange.400">
          Upload an image in the Images tab, then pick it here. Images must be hosted (https) to work in Gmail.
        </Text>
      )}
      <HStack spacing={2} align="flex-end">
        <NumField label="Width" value={block.width} min={LIMITS.imageSize.min} max={LIMITS.imageSize.max}
          onChange={(w) => onChange({ width: w, height: Math.max(LIMITS.imageSize.min, Math.round(w * ratio)) })} flex={1} />
        <NumField label="Height" value={block.height} min={LIMITS.imageSize.min} max={LIMITS.imageSize.max}
          onChange={(h) => onChange({ height: h })} flex={1} />
        <Checkbox size="sm" isChecked={block.circle} onChange={(e) => onChange({ circle: e.target.checked })} pb={1}>
          <Text fontSize="xs">Circle</Text>
        </Checkbox>
      </HStack>
      {naturalSize && (
        <Text fontSize="10px" opacity={0.65}>
          Showing at the image's natural size (imported that way). Type a width/height to pin exact dimensions.
        </Text>
      )}
      <HStack spacing={2}>
        <TextField label="Alt text" value={block.alt} onChange={(alt) => onChange({ alt })} flex={2} />
        <SelectField label="Align" value={block.align || 'left'} flex={1}
          options={[{ value: 'left', label: 'Left' }, { value: 'center', label: 'Center' }, { value: 'right', label: 'Right' }]}
          onChange={(align) => onChange({ align })} />
      </HStack>
      <TextField label="Link (optional)" value={block.href} placeholder="https://…" onChange={(href) => onChange({ href })} />
      <NumField label="Space above (px)" value={block.spacingTop || 0} max={LIMITS.spacing.max} onChange={(spacingTop) => onChange({ spacingTop })} />
    </VStack>
  );
}

function TextForm({ block, onChange }) {
  const segments = Array.isArray(block.segments) && block.segments.length ? block.segments : null;

  const setSegment = (idx, patch) => {
    onChange({ segments: segments.map((s, i) => (i === idx ? { ...s, ...patch } : s)) });
  };
  const plainText = () => segments.map((s) => s.text || '').join('');
  const removeSegment = (idx) => {
    const next = segments.filter((_, i) => i !== idx);
    if (next.length) onChange({ segments: next });
    else onChange({ segments: null, text: plainText() });
  };
  const flatten = () => {
    onChange({ segments: null, text: plainText() });
  };

  if (segments) {
    return (
      <VStack align="stretch" spacing={2}>
        <Field label="Styled runs (this line mixes formatting — edit each part)">
          <VStack align="stretch" spacing={1.5}>
            {segments.map((seg, idx) => (
              <Box key={idx} p={1.5} borderWidth="1px" borderRadius="sm">
                {seg.image ? (
                  // Inline icon flowing with the text (imported signatures).
                  <HStack spacing={2} align="center">
                    <img
                      src={seg.image.src}
                      alt={seg.image.alt || 'Inline icon'}
                      width={seg.image.width || 16}
                      height={seg.image.height || 16}
                      style={{ flexShrink: 0 }}
                    />
                    <Text fontSize="xs" opacity={0.7} noOfLines={1} flex={1}>
                      Inline icon{seg.image.href ? ' (linked)' : ''} — {seg.image.width || '?'}×{seg.image.height || '?'}px
                    </Text>
                    <IconButton size="xs" variant="ghost" icon={<FiTrash2 />} aria-label="Remove icon" onClick={() => removeSegment(idx)} />
                  </HStack>
                ) : (
                  <>
                    <HStack spacing={1} mb={1}>
                      <Input size="xs" value={seg.text || ''} onChange={(e) => setSegment(idx, { text: e.target.value })} />
                      <IconButton size="xs" variant="ghost" icon={<FiTrash2 />} aria-label="Remove run" onClick={() => removeSegment(idx)} />
                    </HStack>
                    <HStack spacing={2} align="flex-end">
                      <Checkbox size="sm" isChecked={!!seg.bold} onChange={(e) => setSegment(idx, { bold: e.target.checked })}>
                        <Text fontSize="xs">B</Text>
                      </Checkbox>
                      <Checkbox size="sm" isChecked={!!seg.italic} onChange={(e) => setSegment(idx, { italic: e.target.checked })}>
                        <Text fontSize="xs">I</Text>
                      </Checkbox>
                      <Checkbox size="sm" isChecked={!!seg.underline} onChange={(e) => setSegment(idx, { underline: e.target.checked })}>
                        <Text fontSize="xs">U</Text>
                      </Checkbox>
                      <ColorField label="" value={seg.color} allowEmpty onChange={(color) => setSegment(idx, { color })} flex={1} />
                      <Input size="xs" value={seg.href || ''} placeholder="link (optional)" flex={2}
                        onChange={(e) => setSegment(idx, { href: e.target.value })} />
                    </HStack>
                  </>
                )}
              </Box>
            ))}
          </VStack>
        </Field>
        <HStack spacing={2}>
          <Button size="xs" variant="outline" leftIcon={<FiPlus />}
            onClick={() => onChange({ segments: [...segments, { text: 'new text' }] })}
            isDisabled={segments.length >= LIMITS.maxSegments}>
            Add run
          </Button>
          <Tooltip label="Merge all runs into one plain-text line (drops per-run formatting)" hasArrow>
            <Button size="xs" variant="ghost" onClick={flatten}>Convert to plain text</Button>
          </Tooltip>
        </HStack>
        <HStack spacing={2} align="flex-end">
          <NumField label="Base size" value={block.size || ''} min={LIMITS.fontSize.min} max={LIMITS.fontSize.max} onChange={(size) => onChange({ size })} flex={1} />
          <ColorField label="Base color" value={block.color} allowEmpty onChange={(color) => onChange({ color })} flex={1} />
          <NumField label="Space above" value={block.spacingTop || 0} max={LIMITS.spacing.max} onChange={(spacingTop) => onChange({ spacingTop })} flex={1} />
        </HStack>
      </VStack>
    );
  }

  return (
    <VStack align="stretch" spacing={2}>
      <Field label="Text">
        <Textarea size="xs" rows={2} value={block.text || ''} onChange={(e) => onChange({ text: e.target.value })} />
      </Field>
      <HStack spacing={2} align="flex-end">
        <NumField label="Size" value={block.size || ''} min={LIMITS.fontSize.min} max={LIMITS.fontSize.max} onChange={(size) => onChange({ size })} flex={1} />
        <ColorField label="Color" value={block.color} allowEmpty onChange={(color) => onChange({ color })} flex={1} />
        <Checkbox size="sm" isChecked={block.bold} onChange={(e) => onChange({ bold: e.target.checked })} pb={1}>
          <Text fontSize="xs">Bold</Text>
        </Checkbox>
        <Checkbox size="sm" isChecked={block.italic} onChange={(e) => onChange({ italic: e.target.checked })} pb={1}>
          <Text fontSize="xs">Italic</Text>
        </Checkbox>
        <Checkbox size="sm" isChecked={block.underline} onChange={(e) => onChange({ underline: e.target.checked })} pb={1}>
          <Text fontSize="xs">U</Text>
        </Checkbox>
      </HStack>
      <TextField label="Link (optional — makes the whole line clickable)" value={block.href} placeholder="https://…" onChange={(href) => onChange({ href })} />
      <NumField label="Space above (px)" value={block.spacingTop || 0} max={LIMITS.spacing.max} onChange={(spacingTop) => onChange({ spacingTop })} />
    </VStack>
  );
}

function ContactForm({ block, onChange }) {
  const items = block.items || [];
  const setItem = (idx, patch) => {
    const next = items.map((it, i) => (i === idx ? { ...it, ...patch } : it));
    onChange({ items: next });
  };
  return (
    <VStack align="stretch" spacing={2}>
      {items.map((item, idx) => (
        <HStack key={idx} spacing={1} align="flex-end">
          <SelectField label={idx === 0 ? 'Type' : ''} value={item.kind} flex={1}
            options={CONTACT_KINDS.map((k) => ({ value: k, label: k }))}
            onChange={(kind) => setItem(idx, { kind })} />
          <TextField label={idx === 0 ? 'Shown text' : ''} value={item.label} onChange={(label) => setItem(idx, { label })} flex={2} />
          <TextField label={idx === 0 ? 'Number / email / URL' : ''} value={item.value} onChange={(value) => setItem(idx, { value })} flex={2} />
          <IconButton size="xs" variant="ghost" icon={<FiTrash2 />} aria-label="Remove item"
            onClick={() => onChange({ items: items.filter((_, i) => i !== idx) })} />
        </HStack>
      ))}
      <Button size="xs" leftIcon={<FiPlus />} variant="outline" alignSelf="flex-start"
        onClick={() => onChange({ items: [...items, { kind: 'phone', label: '', value: '' }] })}
        isDisabled={items.length >= LIMITS.maxContactItems}>
        Add item
      </Button>
      <HStack spacing={2}>
        <TextField label="Separator" value={block.separator} onChange={(separator) => onChange({ separator })} flex={1} />
        <SelectField label="Layout" value={block.layout || 'inline'} flex={1}
          options={[{ value: 'inline', label: 'One line' }, { value: 'stacked', label: 'Stacked' }]}
          onChange={(layout) => onChange({ layout })} />
        <NumField label="Size" value={block.size || ''} min={LIMITS.fontSize.min} max={LIMITS.fontSize.max} onChange={(size) => onChange({ size })} flex={1} />
      </HStack>
      <NumField label="Space above (px)" value={block.spacingTop || 0} max={LIMITS.spacing.max} onChange={(spacingTop) => onChange({ spacingTop })} />
    </VStack>
  );
}

function SocialForm({ block, images, onChange }) {
  const icons = block.icons || [];
  const setIcon = (idx, patch) => {
    onChange({ icons: icons.map((ic, i) => (i === idx ? { ...ic, ...patch } : ic)) });
  };
  return (
    <VStack align="stretch" spacing={2}>
      {icons.map((icon, idx) => (
        <VStack key={idx} align="stretch" spacing={1} p={2} borderWidth="1px" borderRadius="md">
          <HStack spacing={1} align="flex-end">
            <SelectField label="Network" value={icon.network} flex={1}
              options={SOCIAL_NETWORKS.map((n) => ({ value: n.id, label: n.label }))}
              onChange={(network) => {
                const net = SOCIAL_NETWORKS.find((n) => n.id === network);
                setIcon(idx, { network, label: icon.label || net?.label });
              }} />
            <TextField label="Shown text" value={icon.label} onChange={(label) => setIcon(idx, { label })} flex={1} />
            <IconButton size="xs" variant="ghost" icon={<FiTrash2 />} aria-label="Remove"
              onClick={() => onChange({ icons: icons.filter((_, i) => i !== idx) })} />
          </HStack>
          <TextField label="Profile URL" value={icon.href} placeholder="https://linkedin.com/in/…" onChange={(href) => setIcon(idx, { href })} />
          <SelectField label="Icon image (optional — text link when empty)" value={icon.iconUrl || ''}
            options={[{ value: '', label: 'Text link' }, ...images.map((img) => ({ value: img.url, label: img.name }))]}
            onChange={(iconUrl) => setIcon(idx, { iconUrl })} />
        </VStack>
      ))}
      <Button size="xs" leftIcon={<FiPlus />} variant="outline" alignSelf="flex-start"
        onClick={() => onChange({ icons: [...icons, { network: 'linkedin', label: 'LinkedIn', href: '', iconUrl: '' }] })}
        isDisabled={icons.length >= LIMITS.maxSocialIcons}>
        Add link
      </Button>
      <HStack spacing={2}>
        <NumField label="Icon size" value={block.size} min={LIMITS.iconSize.min} max={LIMITS.iconSize.max} onChange={(size) => onChange({ size })} flex={1} />
        <NumField label="Gap" value={block.gap} max={24} onChange={(gap) => onChange({ gap })} flex={1} />
        <NumField label="Space above" value={block.spacingTop || 0} max={LIMITS.spacing.max} onChange={(spacingTop) => onChange({ spacingTop })} flex={1} />
      </HStack>
    </VStack>
  );
}

function ButtonForm({ block, onChange }) {
  return (
    <VStack align="stretch" spacing={2}>
      <HStack spacing={2}>
        <TextField label="Button text" value={block.text} onChange={(text) => onChange({ text })} flex={1} />
        <TextField label="Link" value={block.href} placeholder="https://…" onChange={(href) => onChange({ href })} flex={1} />
      </HStack>
      <HStack spacing={2}>
        <ColorField label="Background" value={block.bg} onChange={(bg) => onChange({ bg })} flex={1} />
        <ColorField label="Text color" value={block.color} onChange={(color) => onChange({ color })} flex={1} />
        <NumField label="Corner radius" value={block.radius} max={LIMITS.radius.max} onChange={(radius) => onChange({ radius })} flex={1} />
      </HStack>
      <HStack spacing={2}>
        <NumField label="Pad X" value={block.paddingX} min={4} max={40} onChange={(paddingX) => onChange({ paddingX })} flex={1} />
        <NumField label="Pad Y" value={block.paddingY} min={2} max={24} onChange={(paddingY) => onChange({ paddingY })} flex={1} />
        <NumField label="Font size" value={block.size} min={LIMITS.fontSize.min} max={LIMITS.fontSize.max} onChange={(size) => onChange({ size })} flex={1} />
        <SelectField label="Align" value={block.align || 'left'} flex={1}
          options={[{ value: 'left', label: 'Left' }, { value: 'center', label: 'Center' }, { value: 'right', label: 'Right' }]}
          onChange={(align) => onChange({ align })} />
      </HStack>
      <NumField label="Space above (px)" value={block.spacingTop || 0} max={LIMITS.spacing.max} onChange={(spacingTop) => onChange({ spacingTop })} />
    </VStack>
  );
}

function DividerForm({ block, onChange }) {
  return (
    <HStack spacing={2} align="flex-end">
      <ColorField label="Color" value={block.color} onChange={(color) => onChange({ color })} flex={1} />
      <NumField label="Thickness" value={block.thickness} min={1} max={LIMITS.thickness.max} onChange={(thickness) => onChange({ thickness })} flex={1} />
      <NumField label="Space above" value={block.spacingTop || 0} max={LIMITS.spacing.max} onChange={(spacingTop) => onChange({ spacingTop })} flex={1} />
      <NumField label="Space below" value={block.spacingBottom || 0} max={LIMITS.spacing.max} onChange={(spacingBottom) => onChange({ spacingBottom })} flex={1} />
    </HStack>
  );
}

function SpacerForm({ block, onChange }) {
  return <NumField label="Height (px)" value={block.height} min={2} max={LIMITS.spacing.max} onChange={(height) => onChange({ height })} />;
}

function DisclaimerForm({ block, onChange }) {
  return (
    <VStack align="stretch" spacing={2}>
      <Field label="Disclaimer text (new lines allowed)">
        <Textarea size="xs" rows={3} value={block.text || ''} onChange={(e) => onChange({ text: e.target.value })} />
      </Field>
      <HStack spacing={2}>
        <NumField label="Size" value={block.size} min={8} max={14} onChange={(size) => onChange({ size })} flex={1} />
        <ColorField label="Color" value={block.color} onChange={(color) => onChange({ color })} flex={1} />
        <NumField label="Space above" value={block.spacingTop || 0} max={LIMITS.spacing.max} onChange={(spacingTop) => onChange({ spacingTop })} flex={1} />
      </HStack>
    </VStack>
  );
}

const BLOCK_FORMS = {
  image: ImageForm,
  text: TextForm,
  contact: ContactForm,
  social: SocialForm,
  button: ButtonForm,
  divider: DividerForm,
  spacer: SpacerForm,
  disclaimer: DisclaimerForm,
};

// ---------------------------------------------------------------------------
// Column settings popover
// ---------------------------------------------------------------------------

function ColumnSettings({ column, onUpdateColumn }) {
  return (
    <Popover placement="bottom-end">
      <PopoverTrigger>
        <IconButton size="xs" variant="ghost" icon={<FiSettings />} aria-label="Column settings" />
      </PopoverTrigger>
      <PopoverContent w="240px" zIndex={30}>
        <PopoverArrow />
        <PopoverBody>
          <VStack align="stretch" spacing={2}>
            <HStack spacing={2}>
              <NumField label="Width (blank = auto)" value={column.width ?? ''} min={20} max={600}
                onChange={(width) => onUpdateColumn(column.id, { width })} flex={1} />
              <SelectField label="V-align" value={column.valign} flex={1}
                options={[{ value: 'top', label: 'Top' }, { value: 'middle', label: 'Middle' }, { value: 'bottom', label: 'Bottom' }]}
                onChange={(valign) => onUpdateColumn(column.id, { valign })} />
            </HStack>
            <HStack spacing={2}>
              <SelectField label="Text align" value={column.align} flex={1}
                options={[{ value: 'left', label: 'Left' }, { value: 'center', label: 'Center' }, { value: 'right', label: 'Right' }]}
                onChange={(align) => onUpdateColumn(column.id, { align })} />
              <NumField label="Pad left" value={column.paddingLeft || 0} max={LIMITS.spacing.max}
                onChange={(paddingLeft) => onUpdateColumn(column.id, { paddingLeft })} flex={1} />
              <NumField label="Pad right" value={column.paddingRight || 0} max={LIMITS.spacing.max}
                onChange={(paddingRight) => onUpdateColumn(column.id, { paddingRight })} flex={1} />
            </HStack>
            <Checkbox size="sm" isChecked={!!column.borderLeft}
              onChange={(e) => onUpdateColumn(column.id, { borderLeft: e.target.checked ? { color: '#0f4c81', thickness: 3 } : null })}>
              <Text fontSize="xs">Accent bar on left edge</Text>
            </Checkbox>
            {column.borderLeft && (
              <HStack spacing={2}>
                <ColorField label="Bar color" value={column.borderLeft.color}
                  onChange={(color) => onUpdateColumn(column.id, { borderLeft: { ...column.borderLeft, color } })} flex={1} />
                <NumField label="Bar width" value={column.borderLeft.thickness} min={1} max={LIMITS.thickness.max}
                  onChange={(thickness) => onUpdateColumn(column.id, { borderLeft: { ...column.borderLeft, thickness } })} flex={1} />
              </HStack>
            )}
            <Text fontSize="10px" opacity={0.6}>Clear width by setting 0. Use "Pad right" to create the gap between columns.</Text>
          </VStack>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
}

// ---------------------------------------------------------------------------
// Main panel
// ---------------------------------------------------------------------------

export default function BlockEditorPanel({
  spec,
  images,
  selectedBlockId,
  onSelectBlock,
  onUpdateGlobal,
  onUpdateBlock,
  onAddBlock,
  onRemoveBlock,
  onMoveBlock,
  onUpdateColumn,
  onAddRow,
  onRemoveRow,
  onApplyTemplate,
}) {
  const chipBG = useColorModeValue('gray.50', 'gray.700');
  const chipSelectedBG = useColorModeValue('purple.50', 'purple.900');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  const selected = useMemo(() => (selectedBlockId ? findBlock(spec, selectedBlockId) : null), [spec, selectedBlockId]);
  const SelectedForm = selected ? BLOCK_FORMS[selected.block.type] : null;

  return (
    <VStack align="stretch" spacing={3}>
      <Accordion allowMultiple defaultIndex={[1, 2]} reduceMotion>
        {/* Templates */}
        <AccordionItem border="none">
          <AccordionButton px={1}>
            <Text flex={1} textAlign="left" fontSize="sm" fontWeight="bold">Templates</Text>
            <AccordionIcon />
          </AccordionButton>
          <AccordionPanel px={1} pb={3}>
            <Wrap spacing={2}>
              {TEMPLATES.map((t) => (
                <WrapItem key={t.id}>
                  <Tooltip label={`${t.description} Replaces the current layout (undo restores it).`} placement="top" hasArrow>
                    <Button size="xs" variant="outline" onClick={() => onApplyTemplate(t.id)}>{t.name}</Button>
                  </Tooltip>
                </WrapItem>
              ))}
            </Wrap>
          </AccordionPanel>
        </AccordionItem>

        {/* Global styles */}
        <AccordionItem border="none">
          <AccordionButton px={1}>
            <Text flex={1} textAlign="left" fontSize="sm" fontWeight="bold">Global styles</Text>
            <AccordionIcon />
          </AccordionButton>
          <AccordionPanel px={1} pb={3}>
            <VStack align="stretch" spacing={2}>
              <HStack spacing={2}>
                <SelectField label="Font" value={spec.global.fontId} flex={2}
                  options={WEB_SAFE_FONTS.map((f) => ({ value: f.id, label: f.label }))}
                  onChange={(fontId) => onUpdateGlobal({ fontId })} />
                <NumField label="Base size" value={spec.global.fontSize} min={LIMITS.fontSize.min} max={LIMITS.fontSize.max}
                  onChange={(fontSize) => onUpdateGlobal({ fontSize })} flex={1} />
              </HStack>
              <HStack spacing={2}>
                <ColorField label="Text" value={spec.global.textColor} onChange={(textColor) => onUpdateGlobal({ textColor })} flex={1} />
                <ColorField label="Links" value={spec.global.linkColor} onChange={(linkColor) => onUpdateGlobal({ linkColor })} flex={1} />
                <ColorField label="Accent" value={spec.global.accentColor} onChange={(accentColor) => onUpdateGlobal({ accentColor })} flex={1} />
              </HStack>
              <Text fontSize="10px" opacity={0.6}>
                Web-safe fonts only — Gmail ignores custom fonts. Midtone colors survive dark mode best.
              </Text>
            </VStack>
          </AccordionPanel>
        </AccordionItem>

        {/* Layout tree */}
        <AccordionItem border="none">
          <AccordionButton px={1}>
            <Text flex={1} textAlign="left" fontSize="sm" fontWeight="bold">Layout</Text>
            <AccordionIcon />
          </AccordionButton>
          <AccordionPanel px={1} pb={3}>
            <VStack align="stretch" spacing={3}>
              {spec.rows.map((row, rowIdx) => (
                <Box key={row.id} borderWidth="1px" borderColor={borderColor} borderRadius="md" p={2}>
                  <Flex align="center" mb={2}>
                    <Text fontSize="xs" fontWeight="bold" opacity={0.7}>Row {rowIdx + 1}</Text>
                    <Box flex={1} />
                    <Menu>
                      <MenuButton as={IconButton} size="xs" variant="ghost" icon={<FiMoreVertical />} aria-label="Row options" />
                      <MenuList zIndex={30}>
                        <MenuItem icon={<FiTrash2 />} color="red.400" onClick={() => onRemoveRow(row.id)}>Delete row</MenuItem>
                      </MenuList>
                    </Menu>
                  </Flex>
                  <HStack align="flex-start" spacing={2}>
                    {row.columns.map((col, colIdx) => (
                      <Box key={col.id} flex={1} minW={0} borderWidth="1px" borderColor={borderColor} borderRadius="md" p={1.5} borderStyle="dashed">
                        <Flex align="center" mb={1}>
                          <Text fontSize="10px" opacity={0.6}>Col {colIdx + 1}{col.width ? ` · ${col.width}px` : ''}</Text>
                          <Box flex={1} />
                          <ColumnSettings column={col} onUpdateColumn={onUpdateColumn} />
                        </Flex>
                        <VStack align="stretch" spacing={1}>
                          {col.blocks.map((block) => {
                            const meta = BLOCK_META[block.type] || {};
                            const BlockIcon = meta.icon || FiType;
                            const isSel = block.id === selectedBlockId;
                            return (
                              <HStack
                                key={block.id}
                                spacing={1.5}
                                px={1.5}
                                py={1}
                                borderRadius="sm"
                                bg={isSel ? chipSelectedBG : chipBG}
                                borderWidth="1px"
                                borderColor={isSel ? 'purple.400' : 'transparent'}
                                cursor="pointer"
                                onClick={() => onSelectBlock(isSel ? null : block.id)}
                              >
                                <BlockIcon size={11} style={{ flexShrink: 0 }} />
                                <Text fontSize="10px" isTruncated>{blockSummary(block)}</Text>
                              </HStack>
                            );
                          })}
                          <Menu>
                            <MenuButton as={Button} size="xs" variant="ghost" leftIcon={<FiPlus />} rightIcon={<FiChevronDown />} fontSize="10px" h="20px">
                              Add
                            </MenuButton>
                            <MenuList zIndex={30}>
                              {BLOCK_TYPES.map((type) => {
                                const meta = BLOCK_META[type];
                                const MenuIcon = meta.icon;
                                return (
                                  <MenuItem key={type} icon={<MenuIcon />} onClick={() => onAddBlock(col.id, type)}>
                                    {meta.label}
                                  </MenuItem>
                                );
                              })}
                            </MenuList>
                          </Menu>
                        </VStack>
                      </Box>
                    ))}
                  </HStack>
                </Box>
              ))}
              <HStack spacing={2}>
                <Button size="xs" variant="outline" leftIcon={<FiPlus />} onClick={() => onAddRow({ columnCount: 1 })}>Add row</Button>
                <Button size="xs" variant="outline" leftIcon={<FiPlus />} onClick={() => onAddRow({ columnCount: 2 })}>Add 2-column row</Button>
              </HStack>
            </VStack>
          </AccordionPanel>
        </AccordionItem>
      </Accordion>

      {/* Selected block form */}
      {selected && SelectedForm && (
        <Box borderWidth="1px" borderColor="purple.300" borderRadius="md" p={3}>
          <Flex align="center" mb={2}>
            <Text fontSize="sm" fontWeight="bold">{BLOCK_META[selected.block.type]?.label || selected.block.type}</Text>
            <Text fontSize="10px" opacity={0.5} ml={2}>({selected.block.id})</Text>
            <Box flex={1} />
            <Tooltip label="Move up">
              <IconButton size="xs" variant="ghost" icon={<FiArrowUp />} aria-label="Move up"
                isDisabled={selected.index === 0}
                onClick={() => onMoveBlock({ blockId: selected.block.id, toColumnId: selected.column.id, position: selected.index - 1 })} />
            </Tooltip>
            <Tooltip label="Move down">
              <IconButton size="xs" variant="ghost" icon={<FiArrowDown />} aria-label="Move down"
                isDisabled={selected.index >= selected.column.blocks.length - 1}
                onClick={() => onMoveBlock({ blockId: selected.block.id, toColumnId: selected.column.id, position: selected.index + 1 })} />
            </Tooltip>
            <Tooltip label="Delete block">
              <IconButton size="xs" variant="ghost" colorScheme="red" icon={<FiTrash2 />} aria-label="Delete block"
                onClick={() => onRemoveBlock(selected.block.id)} />
            </Tooltip>
          </Flex>
          <Divider mb={2} />
          <SelectedForm
            block={selected.block}
            images={images}
            onChange={(patch) => onUpdateBlock(selected.block.id, patch)}
          />
        </Box>
      )}
      {!selected && (
        <HStack spacing={2} p={2} borderWidth="1px" borderStyle="dashed" borderColor={borderColor} borderRadius="md">
          <FiLink size={12} />
          <Text fontSize="xs" opacity={0.7}>
            Click a block above (or in the preview) to edit it — or just tell FutureBot what you want changed.
          </Text>
        </HStack>
      )}
    </VStack>
  );
}
